/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { WaggleActivityTaskReq } from './waggle-activity-task-req'
import { WaggleActivityTask } from './waggle-activity-task'
import { WaggleLog } from './waggle-log'
import { UploadFile } from './upload-file'
import { SearchBatchNo } from './search-batch-no'
import { CheckWaggle } from './check-waggle'
import { LoadWaggleActivityTaskQuery } from './load-waggle-activity-task-query'
import { ExportWaggleReflectionsQuery } from './export-waggle-reflections-query'

const buildConfig = createConfigBuilder('growp')

export const waggleApi = {
  createWaggleActivityTaskConfig(
    waggleActivityTaskReq: WaggleActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/waggle/activity-tasks',
      {},
      null,
      waggleActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          task: (v: any) => v || null,
          batchs: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【后台】摇号-创建活动任务
   * @param waggleActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<WaggleActivityTask>
   */
  createWaggleActivityTask(
    waggleActivityTaskReq: WaggleActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<WaggleActivityTask> {
    return axios(
      waggleApi.createWaggleActivityTaskConfig(waggleActivityTaskReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new WaggleActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateWaggleActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: WaggleActivityTaskReq) =>
        waggleApi.createWaggleActivityTask(payload),
      mutationOptions
    )
  },

  updateWaggleActivityTaskConfig(
    id: number,
    waggleActivityTaskReq: WaggleActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/waggle/activity-tasks/{id}',
      {
        id,
      },
      null,
      waggleActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          task: (v: any) => v || null,
          batchs: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【后台】摇号-编辑活动任务
   * @param id
   * @param waggleActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<WaggleActivityTask>
   */
  updateWaggleActivityTask(
    id: number,
    waggleActivityTaskReq: WaggleActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<WaggleActivityTask> {
    return axios(
      waggleApi.updateWaggleActivityTaskConfig(
        id,
        waggleActivityTaskReq,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new WaggleActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateWaggleActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: WaggleActivityTaskReq }) =>
        waggleApi.updateWaggleActivityTask(id, payload),
      mutationOptions
    )
  },

  loadWaggleActivityTaskConfig(
    id: number,
    loadWaggleActivityTaskQuery?: LoadWaggleActivityTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/waggle/activity-tasks/{id}',
      {
        id,
      },
      loadWaggleActivityTaskQuery
    )
  },

  /**
   * @summary 【后台】摇号-查看活动任务
   *  - 后台：活动任务+批次
   * @param id
   * @param [loadWaggleActivityTaskQuery]
   * @param [loadWaggleActivityTaskQuery.expand] expand
   *  - 活动任务关联的活动人员 task.task_reflections
   *  - 活动任务关联的管理人员 task.admin_reflections
   * @param [options]
   * @return AxiosResponsePromise<WaggleActivityTask>
   */
  loadWaggleActivityTask<TQuery extends LoadWaggleActivityTaskQuery>(
    id: number,
    loadWaggleActivityTaskQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<WaggleActivityTask, TQuery['expand']>> {
    return axios(
      waggleApi.loadWaggleActivityTaskConfig(
        id,
        loadWaggleActivityTaskQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new WaggleActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadWaggleActivityTask<TQuery extends LoadWaggleActivityTaskQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<WaggleActivityTask, TQuery['expand']>> {
    return useQueryApi<Expand<WaggleActivityTask, TQuery['expand']>>(
      waggleApi.loadWaggleActivityTask,
      'Waggle.loadWaggleActivityTask',
      queryOptions,
      id,
      query
    )
  },

  deleteWaggleActivityTaskConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/waggle/activity-tasks/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】摇号-删除活动任务
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteWaggleActivityTask(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(waggleApi.deleteWaggleActivityTaskConfig(id, options)) as any
  },

  useDeleteWaggleActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => waggleApi.deleteWaggleActivityTask(id),
      mutationOptions
    )
  },

  batchCleanNoConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'delete',
      '/waggle/activity-tasks/{id}/clean/$batch',
      {
        id,
      }
    )
  },

  /**
   * @summary 【后台】摇号-摇号界面-批量清空随机码
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  batchCleanNo(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(waggleApi.batchCleanNoConfig(id, options)) as any
  },

  useBatchCleanNo(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => waggleApi.batchCleanNo(id),
      mutationOptions
    )
  },

  batchCreateNoConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/waggle/activity-tasks/{id}/create/$batch',
      {
        id,
      }
    )
  },

  /**
   * @summary 【后台】摇号-摇号界面-生成随机码
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  batchCreateNo(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(waggleApi.batchCreateNoConfig(id, options)) as any
  },

  useBatchCreateNo(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => waggleApi.batchCreateNo(id),
      mutationOptions
    )
  },

  exportWaggleReflectionsConfig(
    id: number,
    exportWaggleReflectionsQuery?: ExportWaggleReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/waggle/activity-tasks/{id}/export-reflections',
      {
        id,
      },
      exportWaggleReflectionsQuery
    )
  },

  /**
   * @summary 【后台】摇号-摇号界面-导出参与用户
   * @param id
   * @param [exportWaggleReflectionsQuery]
   * @param [exportWaggleReflectionsQuery.waggleId] 如不传，默认导出最新的摇号结果
   * @param [exportWaggleReflectionsQuery.isPrize] 是否查询中签结果
   * @param [exportWaggleReflectionsQuery.filename] 不带后缀文件名
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  exportWaggleReflections(
    id: number,
    exportWaggleReflectionsQuery?: ExportWaggleReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      waggleApi.exportWaggleReflectionsConfig(
        id,
        exportWaggleReflectionsQuery,
        options
      )
    ) as any
  },

  useExportWaggleReflections(
    {
      id,
      query,
    }: {
      id: number
      query?: ExportWaggleReflectionsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      waggleApi.exportWaggleReflections,
      'Waggle.exportWaggleReflections',
      queryOptions,
      id,
      query
    )
  },

  queryWaggleLogsConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/waggle/activity-tasks/{id}/logs', {
      id,
    })
  },

  /**
   * @summary 【后台】摇号-摇号界面-摇号历史
   *   - 不支持分页
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<WaggleLog[]>
   */
  queryWaggleLogs(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<WaggleLog[]> {
    return axios(waggleApi.queryWaggleLogsConfig(id, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new WaggleLog(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryWaggleLogs(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<WaggleLog[]> {
    return useQueryApi<WaggleLog[]>(
      waggleApi.queryWaggleLogs,
      'Waggle.queryWaggleLogs',
      queryOptions,
      id
    )
  },

  downloadImportWaggleTemplateConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/waggle/activity-tasks/{id}/reflections/download-template',
      {
        id,
      }
    )
  },

  /**
   * @summary 【后台】下载导入摇号用户模板接口
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  downloadImportWaggleTemplate(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      waggleApi.downloadImportWaggleTemplateConfig(id, options)
    ) as any
  },

  useDownloadImportWaggleTemplate(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      waggleApi.downloadImportWaggleTemplate,
      'Waggle.downloadImportWaggleTemplate',
      queryOptions,
      id
    )
  },

  importWaggleReflectionConfig(
    id: number,
    uploadFile: UploadFile,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/waggle/activity-tasks/{id}/reflections/import',
      {
        id,
      },
      null,
      uploadFile,
      {
        format: 'form',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】导入摇号用户
   *     - 一个用户只能参与一个批次
   * @param id
   * @param uploadFile
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  importWaggleReflection(
    id: number,
    uploadFile: UploadFile,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      waggleApi.importWaggleReflectionConfig(id, uploadFile, options)
    ) as any
  },

  useImportWaggleReflection(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: UploadFile }) =>
        waggleApi.importWaggleReflection(id, payload),
      mutationOptions
    )
  },

  searchBatchNoConfig(
    name: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/waggle/activity-tasks/{id}/search', {
      name,
      id,
    })
  },

  /**
   * @summary 【后台】摇号-摇号界面-查询随机码
   * @param name 用户姓名
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<SearchBatchNo[]>
   */
  searchBatchNo(
    name: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<SearchBatchNo[]> {
    return axios(waggleApi.searchBatchNoConfig(name, id, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new SearchBatchNo(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useSearchBatchNo(
    {
      name,
      id,
    }: {
      name: string
      id: number
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<SearchBatchNo[]> {
    return useQueryApi<SearchBatchNo[]>(
      waggleApi.searchBatchNo,
      'Waggle.searchBatchNo',
      queryOptions,
      name,
      id
    )
  },

  getWaggleConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/waggle/activity-tasks/{id}/waggle', {
      id,
    })
  },

  /**
   * @summary 【后台】摇号-摇号界面-查看进行中摇号
   *  - 查询当前进行中的摇号
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<CheckWaggle>
   */
  getWaggle(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<CheckWaggle> {
    return axios(waggleApi.getWaggleConfig(id, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new CheckWaggle(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useGetWaggle(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<CheckWaggle> {
    return useQueryApi<CheckWaggle>(
      waggleApi.getWaggle,
      'Waggle.getWaggle',
      queryOptions,
      id
    )
  },

  batchWaggleConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/waggle/activity-tasks/{id}/waggle/$batch',
      {
        id,
      }
    )
  },

  /**
   * @summary 【后台】摇号-摇号界面-摇号
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  batchWaggle(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(waggleApi.batchWaggleConfig(id, options)) as any
  },

  useBatchWaggle(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => waggleApi.batchWaggle(id),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(waggleApi).forEach((api) => {
  // @ts-ignore
  waggleApi[api]._name_ = `Waggle.${api}`
})
